import React, { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseDateValue, DATE_RANGE, getCommonTextFieldStyles } from '../../_utils/fieldInputHelpers';
import dayjs, { Dayjs } from 'dayjs';

export interface BaseDateInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

const BaseDateInput: React.FC<BaseDateInputProps> = ({
  value,
  onChange,
  label = 'Select date',
  required = false,
  disabled = false,
  error,
  helperText,
  size = 'small',
  fullWidth = true,
}) => {
  const [pickerError, setPickerError] = useState<string | null>(null);

  const dateValue = parseDateValue(value);

  // Map picker errors to proper translation messages (same as original DateField)
  const pickerErrorText =
    pickerError === 'invalidDate'
      ? 'Please enter a valid date'
      : pickerError === 'minDate' || pickerError === 'maxDate'
        ? 'Date must be between 01/01/1900 and 12/31/2199'
        : '';

  const handleChange = (date: Dayjs | null) => {
    if (date) {
      // Always update the value, let react-hook-form validation handle errors
      if (date.isValid()) {
        // Store as ISO date string (YYYY-MM-DD format) to match backend expectations
        onChange(date.format('YYYY-MM-DD'));
      } else {
        // For invalid dates, store empty string and let form validation catch it
        onChange('');
      }
    } else {
      onChange('');
    }
  };

  // Combine form validation error and picker error (prioritize form error)
  const showError = !!error || !!pickerError;
  const displayHelperText = error || pickerErrorText || helperText;

  return (
    <DatePicker
      label={label}
      value={dateValue}
      onChange={handleChange}
      onError={(reason) => setPickerError(reason)} // Capture picker validation errors
      format='MM/DD/YYYY'
      minDate={DATE_RANGE.MIN}
      maxDate={DATE_RANGE.MAX}
      disabled={disabled}
      slotProps={{
        textField: {
          fullWidth,
          size,
          error: showError,
          helperText: displayHelperText,
          placeholder: 'MM/DD/YYYY',
          required,
          sx: getCommonTextFieldStyles(),
        },
      }}
    />
  );
};

export default BaseDateInput;
